import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import type { CountryClickStats } from "../lib/types";

/**
 * On-demand free vs. premium click breakdown (all-time / this year / this
 * week / today) for one country, backing the country details panel - see
 * supabase/migrations/20260101000010_country_click_stats.sql. Unlike
 * useCountryClicks, this is NOT wired to Supabase Realtime directly: it's a
 * snapshot, re-fetched whenever `countryNumeric` changes OR `refreshKey`
 * changes. PurchasePanel passes `currentFreeClicks + currentPremiumClicks`
 * (both already realtime, via useCountryClicks in MapPage) as that key, so
 * this breakdown re-fetches piggybacking on the SAME realtime signal - no
 * separate subscription needed for a value this secondary, and it stays
 * correct for every visitor's click on the selected country, not just the
 * current visitor's own.
 */
export function useCountryClickStats(countryNumeric: number | null, refreshKey?: unknown) {
  const [stats, setStats] = useState<CountryClickStats | null>(null);
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async () => {
    if (countryNumeric === null) {
      setStats(null);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .rpc("get_country_click_stats", { p_country_numeric: countryNumeric })
      .single();
    if (error) {
      console.error("failed to load country click stats", error);
      setStats(null);
    } else {
      setStats(data as CountryClickStats);
    }
    setLoading(false);
  }, [countryNumeric]);

  useEffect(() => {
    void refetch();
    // refreshKey is intentionally in this effect's dependencies (it's what
    // triggers a re-fetch after a click changes the underlying numbers) but
    // NOT in refetch's own useCallback deps above - refetch's logic doesn't
    // read it, only this effect's "when to re-run" decision does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch, refreshKey]);

  return { stats, loading, refetch };
}
